import { BottomNavigation, BottomNavigationAction, Box, TextField, Tooltip } from '@mui/material';
import List from 'widgets/list/list';
import styles from 'pages/insured-persons/InsuredPersons.module.css';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContractsIDPolicies, CurrentPagePoliciesInCase, Download, ErrorDownload, FileData, FileName, InsuredPersonsList, LastFetchPolicies, LoadingPolicies } from 'entities/insured-persons/selector';
import { fetchInsuredPersonsList, fetchPoliciesDownload } from 'entities/insured-persons/actions/fetch-insured-persons';
import { resetFileData, setCurrentPageInCaseInsuredPersons } from 'entities/insured-persons';
import TransferList from 'widgets/field-settings/FieldSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import ModalWidget from 'widgets/modal/Modal';
import { allFields, defaultFields, fieldMappings } from 'features/insured-persons/utils/constant';
import { isEqual } from 'lodash-es';
import { IStateProps } from 'shared/types/interface/interface';
import { IPolicies } from 'entities/insured-persons/types/InsuredPersons-interface';
import SearchIcon from '@mui/icons-material/Search';

import appStyles from 'app/providers/theme/App.module.css';
import CustomButton from 'shared/custom-button/CustomButton';

import { isValidIdCase } from 'shared/utils/hooks/useValidationIdCase';
import Loader from 'shared/loader/Loader';
import { AlertMessage } from 'shared/alert-message/alert-message';
import FileDownloadIcon from '@mui/icons-material/FileDownload';


interface IInsuredPersons {
    idContract: string[];
}
interface IAlertInfo {
    message?: string;
    severity?: string;
}

export const InsuredPersonsCase: FC<IInsuredPersons> = ({ idContract }) => {
    const limit = 10;
    const dispatch = useAppDispatch();

    const insuredPersons = useAppSelector(InsuredPersonsList);
    const isLoading = useAppSelector(LoadingPolicies);
    const currentPageInCase = useAppSelector(CurrentPagePoliciesInCase);

    const [selectedOptions, setSelectedOptions] = useState(0);
    const [isUserAction, setIsUserAction] = useState(false);
    const [fields, setFields] = useState<string[]>(defaultFields);
    const [tempFields, setTempFields] = useState<string[]>(fields);
    const [open, setOpen] = useState(false);

    const sortArray: string[] | [] = ['ID'];

    const fetchContractsID = useAppSelector(ContractsIDPolicies, isEqual);
    const isFetching = useRef(false);
    const stableIdContract = useMemo(() => idContract || [], [idContract]);
    const oneDay = 24 * 60 * 60 * 1000;
    const currentContracts = stableIdContract;
    const lastFetch = useAppSelector(LastFetchPolicies);

    const [openSearch, setOpenSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const [alertInfo, setAlertInfo] = useState<IAlertInfo>({ message: '', severity: '' });

    const [downloadTriggered, setDownloadTriggered] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleOpenSearch = () => setOpenSearch((prevState) => !prevState);


    const fileData = useAppSelector(FileData);
    const fileName = useAppSelector(FileName);
    const isDownload = useAppSelector(Download);
    const errorDownload = useAppSelector(ErrorDownload);

    const shouldFetch = () => {
        const isSameParams = isEqual(currentContracts, fetchContractsID);
        const isDataStale = !lastFetch || (Date.now() - lastFetch > oneDay);
        return !isSameParams || isDataStale;
    };

    const currentFilters = useMemo(() => {
        return selectedOptions === 0 ? [] : [];
    }, [selectedOptions]);

    const [isValid, setIsValid] = useState(true);

    const fetchTasksWithValidation = (idContract: string[]) => {
        if (idContract.every(isValidIdCase)) {
            const contract = idContract;
            dispatch(fetchInsuredPersonsList({ contractID: contract, sortArray, filters: currentFilters }))
        } else {
            setIsValid(false);
            console.error('Invalid idCase');
        }
    };

    useEffect(() => {
        if (isFetching.current) return;
        if (shouldFetch()) {
            isFetching.current = true;
            new Promise<void>((resolve) => {
                fetchTasksWithValidation(idContract);
                resolve();
            }).finally(() => {
                isFetching.current = false;
            });
        }
    }, [dispatch, stableIdContract, lastFetch, fetchContractsID, currentPageInCase, selectedOptions]);
    
    // useEffect(() => {
    //     if (isFetching.current) return;
    //     if (shouldFetch()) {
    //         isFetching.current = true;
    //         fetchTasksWithValidation(idContract);
    //         isFetching.current = false;
    //     }
    // }, [dispatch, stableIdContract, lastFetch, fetchContractsID, currentPageInCase, selectedOptions]);

    const handleChildAction = useCallback((page: number) => {
        dispatch(setCurrentPageInCaseInsuredPersons({ page }));
    }, [dispatch]);


    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        dispatch(setCurrentPageInCaseInsuredPersons({ page: 1 }));
    }, [dispatch]);

    // const filteredInsuredPersons = useMemo(() => {
    //     let filteredData = [...insuredPersons];
    //     if (selectedOptions !== 0) {
    //         filteredData = filteredData.filter(person => person.Active === (selectedOptions === 1));
    //     }
    //     if (searchQuery) {
    //         filteredData = filteredData.filter((item: IPolicies) =>
    //             item.PolicyNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    //             item.FullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    //             item.InsuranceProgramPrintName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    //             item.InsurerName.toLowerCase().includes(searchQuery.toLowerCase())
    //         );
    //     }
    //     return filteredData;
    // }, [selectedOptions, insuredPersons, searchQuery]);

    // const paginatedInsuredPersons = useMemo(() => {
    //     const startIndex = (currentPageInCase - 1) * limit;
    //     return filteredInsuredPersons.slice(startIndex, startIndex + limit);
    // }, [currentPageInCase, filteredInsuredPersons]);

    const filteredInsuredPersons = (() => {
        let filteredData = [...insuredPersons];
        if (selectedOptions !== 0) {
            filteredData = filteredData.filter(person => person.Active === (selectedOptions === 1));
        }
        if (searchQuery) {
            filteredData = filteredData.filter((item: IPolicies) =>
                item.PolicyNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.FullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.InsuranceProgramPrintName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.InsurerName.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        return filteredData;
    })();

    const paginatedInsuredPersons = () => {
        const startIndex = (currentPageInCase - 1) * limit;
        return filteredInsuredPersons.slice(startIndex, startIndex + limit);
    };


    const renderItem = (item: any) => (
        <>
            {fields.map((field) => {
                const key = fieldMappings[field];
                if (key === 'StartDateEndDate') {
                    return (
                        <Box key={field}>
                            <p>{`${item.StartDate} - ${item.EndDate}`}</p>
                        </Box>
                    );
                } else if (key === 'Active') {
                    return (
                        <Box key={field}>
                            <p>{item[key] ? 'Прикреплен' : 'Откреплен'}</p>
                        </Box>
                    );
                } else {
                    return (
                        <Box key={field}>
                            <p>{item[key]}</p>
                        </Box>
                    );
                }
            })}
        </>
    );

    const handleOpen = () => {
        setTempFields(fields);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleReset = () => {
        setTempFields(defaultFields);
    };

    const handleAccept = () => {
        setFields(tempFields);
        setOpen(false);
    };

    const handleTempFieldsChange = (newFields: string[]) => {
        setTempFields(newFields);
    };

    const state: IStateProps = {
        fromInsuranceContractPage: true,
        id: idContract ? idContract[0] : undefined,
        activeTab: 4
    };

    const handleUpdate = () => {
        isFetching.current = true;
        fetchTasksWithValidation(idContract);
        isFetching.current = false;
    }
    const handleFileDownload = async (filters: any) => {
        setAlertInfo({ message: '', severity: '' });

        try {
            setIsDownloading(true);

            await dispatch(fetchPoliciesDownload({ Filters: filters }));

            setDownloadTriggered(true);
        } catch (error) {
            console.error('Error downloading the file:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        if (downloadTriggered && fileData && errorDownload.length === 0) {
            const link = document.createElement('a');
            link.href = `data:application/octet-stream;base64,${fileData}`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            dispatch(resetFileData());
            setDownloadTriggered(false);
        } else if (errorDownload.length > 0) {
            setAlertInfo({ message: errorDownload[0].Msg, severity: 'error' });
            const timeoutId = setTimeout(() => {
                setAlertInfo({ message: '', severity: '' });
                dispatch(resetFileData());
            }, 3000);
            return () => clearTimeout(timeoutId);
        }
    }, [downloadTriggered, fileData, errorDownload, fileName, dispatch]);

    const excelDownload = async () => {
        const ids = filteredInsuredPersons.map(object => object.ID);
        const filters = [{
            "Field": "ID",
            "ComparisonType": "IN",
            "Value": ids
        }];

        await handleFileDownload(filters);
    };

    if (!isValid) {
        return (
            <p className={styles.errorText}>Некорректный ID договора</p>
        )
    }

    return (
        <Box>
            {alertInfo.message && (
                <Box className={appStyles.alertBox}>
                    <AlertMessage alertInfo={alertInfo} />
                </Box>
            )}

            {isLoading && (
                <Loader
                    title='Загрузка...'
                />
            )}
            {isDownload && (
                <Loader
                    title='Файл скачивается... Пожалуйста, подождите'
                />
            )}
            <Box className={styles.btnContainer}>
                {!isLoading && !isDownload && (
                    <>
                        {insuredPersons.length > 0 && (
                            <Tooltip title="Выгрузить в Excel" placement="top">
                                <Box>
                                    <CustomButton
                                        icon={FileDownloadIcon}
                                        handleClick={excelDownload}
                                        textColor='white'
                                        backgroundColor="#0054E6"
                                        border='none'
                                        borderRadius='4px'
                                        padding="8px 30px"
                                        disabled={false}
                                    />
                                </Box>
                            </Tooltip>
                        )}

                        <Box className={styles.btnNavigation}>
                            <BottomNavigation
                                showLabels
                                value={selectedOptions}
                                onChange={(event, newValue) => {
                                    setSelectedOptions(newValue);
                                    setIsUserAction(true);
                                }}
                                sx={{
                                    backgroundColor: 'transparent',
                                }}
                            >
                                <BottomNavigationAction label="Весь список" sx={{
                                    '&.Mui-selected': {
                                        color: '#0054E6 !important',
                                    },
                                }} />
                                <BottomNavigationAction label="Прикрепленные" sx={{
                                    '&.Mui-selected': {
                                        color: '#0054E6 !important',
                                    },
                                }} />
                                <BottomNavigationAction label="Открепленные" sx={{
                                    '&.Mui-selected': {
                                        color: '#0054E6 !important',
                                    },
                                }} />
                            </BottomNavigation>
                            <Tooltip title="Настройка таблицы" placement="top">
                                <span
                                    className={styles.iconContract}
                                    onClick={() => handleOpen()}
                                >
                                    <SettingsIcon />
                                </span>
                            </Tooltip>
                        </Box>
                    </>
                )}

            </Box>
            {!isLoading && !isDownload && (
                <>
                    <Box className={appStyles.iconSort}>
                        <span onClick={handleOpenSearch} className={`${openSearch ? appStyles.iconSelected : ''}`}><SearchIcon /></span>
                    </Box>
                    <ModalWidget
                        open={open}
                        handleClose={handleClose}
                        title="Настройка полей таблицы"
                        closeIconPosition="right"
                        showAcceptButton
                        showCancelButton
                        onAccept={handleAccept}
                        onCancel={handleReset}
                        widthProps={650}
                    >
                        <TransferList
                            key={JSON.stringify(tempFields)}
                            defaultFields={tempFields}
                            allFields={allFields}
                            onFieldsChange={handleTempFieldsChange}
                        />
                    </ModalWidget>

                    {openSearch && (
                        <TextField
                            label="Поиск"
                            variant="outlined"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            fullWidth
                            margin="normal"
                        />
                    )}
                    {insuredPersons.length > 0 && filteredInsuredPersons.length > 0 ? (
                        <Box>

                            <Box sx={{
                                width: '100%',
                                overflow: 'hidden',
                                position: 'relative'
                            }}>
                                <List
                                    title={fields}
                                    data={paginatedInsuredPersons()}
                                    renderItem={renderItem}
                                    renderPagination={filteredInsuredPersons.length > 10}
                                    pageCount={Math.ceil(filteredInsuredPersons.length / 10)}
                                    onAction={handleChildAction}
                                    currentPage={currentPageInCase}
                                    pathnameProps="/insured-persons/case/"
                                    link={true}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Box className={appStyles.infoBox}>
                            <p>Застрахованные не найдены</p>
                        </Box>
                    )}
                    <Box className={styles.updateBtnBox}>
                        <CustomButton
                            handleClick={handleUpdate}
                            title='Обновить данные'
                            textColor="#0054E6"
                            backgroundColor="transparent"
                            padding="5px 30px"
                            gap="10px"
                            border='none'
                        />
                    </Box>
                </>
            )}
        </Box>



    );
};

export default InsuredPersonsCase;
